import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detección estándar de "cambios sin guardar" para los formularios del portal.
 *
 * Contexto: antes cada página resolvía esto por su cuenta (o no lo resolvía).
 * TriagePromptPage mostraba "Sin guardar", CampanaComposerPage mostraba
 * "Cambios sin guardar" y el resto —incluida /admin/configuracion— no avisaba
 * nada, así que era fácil editar un campo, no dar Guardar y creer que el cambio
 * se había aplicado.
 *
 * Uso:
 *   const { hayCambios, marcarGuardado } = useCambiosSinGuardar(config);
 *   // al terminar de cargar:      marcarGuardado(configCargada)
 *   // tras guardar correctamente: marcarGuardado()
 *
 * Mientras `hayCambios` sea true se registra un `beforeunload` que avisa al
 * cerrar la pestaña o recargar. La navegación interna NO se intercepta: la app
 * usa <BrowserRouter> y `useBlocker` requiere un data router.
 */
export function useCambiosSinGuardar<T>(
  valorActual: T,
  opciones?: {
    /** Si es false, nunca reporta cambios (p. ej. mientras carga). */
    habilitado?: boolean;
    /** Aviso nativo al cerrar/recargar la pestaña. Por defecto true. */
    avisarAlSalir?: boolean;
  },
) {
  const { habilitado = true, avisarAlSalir = true } = opciones ?? {};

  // Referencia al último estado considerado "guardado". Hasta que no se marque
  // una línea base no se reporta ningún cambio (evita falsos positivos entre el
  // render inicial con valores por defecto y la carga real desde Firestore).
  const referenciaRef = useRef<string | null>(null);
  // Copia cruda del último estado guardado, para que "Descartar" pueda
  // restaurarlo sin que cada página tenga que llevar su propio snapshot.
  const valorGuardadoRef = useRef<T | null>(null);
  // Espejo siempre fresco del valor actual: marcarGuardado() se crea una sola
  // vez (deps []) y sin este ref capturaría el valor del primer render, dejando
  // la línea base en los valores por defecto tras guardar.
  const valorActualRef = useRef<T>(valorActual);
  useEffect(() => {
    valorActualRef.current = valorActual;
  }, [valorActual]);
  const [hayCambios, setHayCambios] = useState(false);

  const serializar = (v: unknown) => {
    try {
      return JSON.stringify(v, (_clave, valor) =>
        // Orden de claves estable: dos objetos equivalentes serializan igual
        // aunque React los haya reconstruido en distinto orden.
        valor && typeof valor === 'object' && !Array.isArray(valor)
          ? Object.keys(valor as Record<string, unknown>)
              .sort()
              .reduce<Record<string, unknown>>((acc, k) => {
                acc[k] = (valor as Record<string, unknown>)[k];
                return acc;
              }, {})
          : valor,
      );
    } catch {
      return null;
    }
  };

  /** Fija el estado actual (o el que se pase) como "guardado". */
  const marcarGuardado = useCallback((valor?: T) => {
    const v = valor === undefined ? valorActualRef.current : valor;
    referenciaRef.current = serializar(v);
    valorGuardadoRef.current = v;
    setHayCambios(false);
  }, []);

  /** Último valor marcado como guardado (para restaurar al descartar). */
  const valorGuardado = useCallback(() => valorGuardadoRef.current, []);

  useEffect(() => {
    if (!habilitado || referenciaRef.current === null) {
      setHayCambios(false);
      return;
    }
    setHayCambios(serializar(valorActual) !== referenciaRef.current);
  }, [valorActual, habilitado]);

  useEffect(() => {
    if (!hayCambios || !avisarAlSalir) return;
    const alSalir = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Los navegadores modernos ignoran el texto y muestran su propio mensaje,
      // pero returnValue sigue siendo necesario para que aparezca el diálogo.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios, avisarAlSalir]);

  return { hayCambios, marcarGuardado, valorGuardado };
}

export default useCambiosSinGuardar;
